import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Info,
    DollarSign,
    Download,
    Search,
    Image as ImageIcon,
    List,
    Star,
    Database,
    Zap,
    Save,
    ArrowLeft,
    Plus,
    X,
    Upload,
    Link as LinkIcon,
    Eye,
    CheckCircle2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const InputField = ({ label, name, value, onChange, type = 'text', placeholder, icon: Icon, options, required }) => (
    <div className="space-y-2 flex-1 min-w-[280px]">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">
            {label.replace(/_/g, ' ')}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="relative group">
            {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-violet-500 transition-colors" size={18} />}
            {type === 'select' ? (
                <select
                    name={name}
                    value={value}
                    onChange={onChange}
                    className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all text-white text-sm appearance-none cursor-pointer placeholder:text-slate-500"
                >
                    {options.map(opt => <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">{opt.label}</option>)}
                </select>
            ) : type === 'textarea' ? (
                <textarea
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    rows={4}
                    className="w-full pl-4 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all text-white text-sm resize-none placeholder:text-slate-500"
                />
            ) : type === 'datetime-local' ? (
                <input
                    type="datetime-local"
                    name={name}
                    value={value}
                    onChange={onChange}
                    className="w-full pl-4 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all text-white text-sm placeholder:text-slate-500"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={`w-full ${Icon ? 'pl-11' : 'pl-4'} pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all text-white text-sm placeholder:text-slate-500`}
                />
            )}
        </div>
    </div>
);

const EditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('basic');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [categories, setCategories] = useState([]);
    const [productTypes, setProductTypes] = useState([]);

    const [formData, setFormData] = useState({
        product_title: '',
        slug: '',
        short_description: '',
        full_description: '',
        category_id: '',
        sub_category_id: '',
        product_type: 'digital',
        version: '1.0.0',
        file_size: '',
        status: 'draft',
        featured: false,
        trending: false,
        regular_price: '',
        sale_price: '',
        discount_percentage: '',
        license_type: 'single',
        license_duration: 'lifetime',
        subscription_type: 'one-time',
        coupon_allowed: true,
        tax_percentage: '0',
        currency: 'USD',
        main_file_url: '',
        preview_file_url: '',
        demo_url: '',
        documentation_url: '',
        download_limit: '-1',
        download_expiry_days: '-1',
        license_key_required: false,
        license_key_prefix: '',
        file_hosting_type: 'local',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        focus_keyword: '',
        seo_slug: '',
        canonical_url: '',
        og_title: '',
        og_description: '',
        og_image: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        schema_type: 'Product',
        robots_meta: 'index, follow',
        thumbnail_image: '',
        gallery_images: '',
        alt_text: '',
        features: [''],
        what_is_included: [''],
        technical_requirements: '',
        installation_guide: '',
        support_period: '6 months',
        compatibility: '',
        rating: '5',
        money_back_guarantee: '30 days',
        faq: [{ q: '', a: '' }],
        tags: '',
        badge_text: '',
        stock_status: 'instock',
        stock_quantity: '999',
        visibility: 'public',
        publish_date: '',
        affiliate_commission: '0',
        auto_update_enabled: false,
        api_access: false,
    });

    const [uploadedFiles, setUploadedFiles] = useState({
        thumbnail_image: null,
        gallery_images: [],
        main_file: null
    });

    const [previewUrls, setPreviewUrls] = useState({
        thumbnail_image: '',
        gallery_images: []
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setFetching(true);
                const [catRes, typeRes, prodRes] = await Promise.all([
                    api.get('/categories'),
                    api.get('/product-types'),
                    api.get(`/products/${id}`)
                ]);

                if (catRes.data.success) {
                    setCategories(catRes.data.data.map(cat => ({ label: cat.name, value: cat._id })));
                }
                if (typeRes.data.success) {
                    setProductTypes(typeRes.data.data.map(type => ({ label: type.name, value: type.slug })));
                }

                if (prodRes.data.success) {
                    const product = prodRes.data.data;

                    // Format publish_date for datetime-local input
                    let formattedDate = '';
                    if (product.publish_date) {
                        const date = new Date(product.publish_date);
                        formattedDate = date.toISOString().slice(0, 16);
                    }

                    setFormData({
                        ...product,
                        category_id: product.category_id?._id || product.category_id || '',
                        features: product.features && product.features.length > 0 ? product.features : [''],
                        what_is_included: product.what_is_included && product.what_is_included.length > 0 ? product.what_is_included : [''],
                        faq: product.faq?.length > 0 ? product.faq.map(f => ({ q: f.question, a: f.answer })) : [{ q: '', a: '' }],
                        tags: product.tags?.join(', ') || '',
                        meta_keywords: product.meta_keywords?.join(', ') || '',
                        publish_date: formattedDate,
                    });

                    if (product.thumbnail_image) {
                        setPreviewUrls(prev => ({
                            ...prev,
                            thumbnail_image: product.thumbnail_image.startsWith('http') ? product.thumbnail_image : `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${product.thumbnail_image}`
                        }));
                    }
                    if (product.gallery_images && Array.isArray(product.gallery_images)) {
                        setPreviewUrls(prev => ({
                            ...prev,
                            gallery_images: product.gallery_images.map(img => img.startsWith('http') ? img : `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${img}`)
                        }));
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load product details');
            } finally {
                setFetching(false);
            }
        };
        fetchData();
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e, fieldName) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        if (fieldName === 'gallery_images') {
            const fileArray = Array.from(files);
            setUploadedFiles(prev => ({ ...prev, gallery_images: [...prev.gallery_images, ...fileArray] }));

            const urls = fileArray.map(file => URL.createObjectURL(file));
            setPreviewUrls(prev => ({ ...prev, gallery_images: [...prev.gallery_images, ...urls] }));
        } else {
            const file = files[0];
            setUploadedFiles(prev => ({ ...prev, [fieldName]: file }));
            if (fieldName === 'thumbnail_image') {
                setPreviewUrls(prev => ({ ...prev, thumbnail_image: URL.createObjectURL(file) }));
            }
        }
    };

    const removeGalleryImage = (index) => {
        const existingCount = Array.isArray(formData.gallery_images) ? formData.gallery_images.length : 0;

        if (index < existingCount) {
            // Removing an existing image
            setFormData(prev => ({
                ...prev,
                gallery_images: prev.gallery_images.filter((_, i) => i !== index)
            }));
        } else {
            // Removing a newly uploaded file
            const fileIndex = index - existingCount;
            setUploadedFiles(prev => ({
                ...prev,
                gallery_images: prev.gallery_images.filter((_, i) => i !== fileIndex)
            }));
        }

        // Always update previews
        setPreviewUrls(prev => ({
            ...prev,
            gallery_images: prev.gallery_images.filter((_, i) => i !== index)
        }));
    };

    const handleArrayChange = (index, value, field) => {
        const newArr = [...formData[field]];
        newArr[index] = value;
        setFormData(prev => ({ ...prev, [field]: newArr }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
    };

    const removeArrayItem = (index, field) => {
        const newArr = formData[field].filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, [field]: newArr.length > 0 ? newArr : [''] }));
    };

    const handleFaqChange = (index, field, value) => {
        const newFaq = [...formData.faq];
        newFaq[index][field] = value;
        setFormData(prev => ({ ...prev, faq: newFaq }));
    };

    const addFaqItem = () => {
        setFormData(prev => ({ ...prev, faq: [...prev.faq, { q: '', a: '' }] }));
    };

    const removeFaqItem = (index) => {
        const newFaq = formData.faq.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, faq: newFaq.length > 0 ? newFaq : [{ q: '', a: '' }] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.product_title.trim()) {
            toast.error('Please enter a product title');
            return;
        }

        if (!formData.category_id) {
            toast.error('Please select a category');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();

            // Add all textual fields
            Object.keys(formData).forEach(key => {
                // Skip files and special handling fields
                if (['thumbnail_image', 'gallery_images', 'main_file', '_id', '__v', 'createdAt', 'updatedAt', 'created_by', 'updated_by'].includes(key)) return;

                const value = formData[key];

                if (Array.isArray(value)) {
                    // Handled separately or as JSON
                    if (key !== 'features' && key !== 'what_is_included' && key !== 'faq') {
                        formDataToSend.append(key, JSON.stringify(value));
                    }
                } else if (typeof value === 'boolean') {
                    formDataToSend.append(key, value);
                } else if (value !== null && value !== undefined && value !== '') {
                    formDataToSend.append(key, value);
                }
            });

            // Numeric conversions (ensuring they are numbers)
            formDataToSend.set('regular_price', parseFloat(formData.regular_price) || 0);
            if (formData.sale_price) formDataToSend.set('sale_price', parseFloat(formData.sale_price));
            if (formData.discount_percentage) formDataToSend.set('discount_percentage', parseFloat(formData.discount_percentage));
            formDataToSend.set('tax_percentage', parseFloat(formData.tax_percentage) || 0);
            formDataToSend.set('download_limit', parseInt(formData.download_limit) || -1);
            formDataToSend.set('download_expiry_days', parseInt(formData.download_expiry_days) || -1);
            formDataToSend.set('stock_quantity', parseInt(formData.stock_quantity) || 999);
            formDataToSend.set('rating', parseFloat(formData.rating) || 5);
            formDataToSend.set('affiliate_commission', parseFloat(formData.affiliate_commission) || 0);

            // Handle Tags & Keywords
            const tags = formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [];
            formDataToSend.set('tags', JSON.stringify(tags));

            const metaKeywords = formData.meta_keywords ? formData.meta_keywords.split(',').map(kw => kw.trim()).filter(Boolean) : [];
            formDataToSend.set('meta_keywords', JSON.stringify(metaKeywords));

            // Handle Arrays
            formDataToSend.set('features', JSON.stringify(formData.features.filter(Boolean)));
            formDataToSend.set('what_is_included', JSON.stringify(formData.what_is_included.filter(Boolean)));
            formDataToSend.set('faq', JSON.stringify(formData.faq.filter(f => f.q && f.a).map(f => ({ question: f.q, answer: f.a }))));

            // Keep existing gallery images (the ones that weren't removed)
            const existingGallery = Array.isArray(formData.gallery_images) ? formData.gallery_images : [];
            formDataToSend.set('existing_gallery_images', JSON.stringify(existingGallery));

            // Add new files
            if (uploadedFiles.thumbnail_image) {
                formDataToSend.append('thumbnail_image', uploadedFiles.thumbnail_image);
            }
            if (uploadedFiles.gallery_images.length > 0) {
                uploadedFiles.gallery_images.forEach(file => {
                    formDataToSend.append('gallery_images', file);
                });
            }
            if (uploadedFiles.main_file) {
                formDataToSend.append('main_file', uploadedFiles.main_file);
            }

            const res = await api.put(`/products/${id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res.data.success) {
                toast.success('Product updated successfully!');
                navigate('/admin/products');
            }
        } catch (error) {
            console.error('Update Error:', error);
            const errorMsg = error.response?.data?.error || 'Failed to update product';
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to update product');
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: Info },
        { id: 'pricing', label: 'Pricing & License', icon: DollarSign },
        { id: 'files', label: 'Files & Access', icon: Download },
        { id: 'seo', label: 'SEO & Meta', icon: Search },
        { id: 'media', label: 'Media', icon: ImageIcon },
        { id: 'features', label: 'Features', icon: List },
        { id: 'marketing', label: 'Marketing', icon: Star },
        { id: 'inventory', label: 'Control', icon: Database },
        { id: 'advanced', label: 'Advanced', icon: Zap },
    ];

    if (fetching) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-medium">Loading Product Data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-20 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-900 p-6 rounded-2xl border border-slate-800 sticky top-4 z-30 mb-8 mx-[-2px] shadow-2xl shadow-slate-950/50">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/products')} className="p-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 hover:text-white transition-all">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">Edit Product</h1>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{formData.product_title || 'Updating Product...'}</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-8 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-violet-600/20 active:scale-[0.98] transition-all flex items-center gap-2"
                    >
                        {loading ? 'Processing...' : <><Save size={18} /> Save Changes</>}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-3 space-y-2">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 text-left group border ${activeTab === tab.id
                                ? 'bg-violet-600 border-violet-500 text-white shadow-lg shadow-violet-900/20'
                                : 'bg-slate-900 border-transparent hover:bg-slate-800 text-slate-400 hover:text-white'}`}
                        >
                            <tab.icon size={20} className={activeTab === tab.id ? 'text-white' : 'text-slate-500 group-hover:text-violet-400 transition-colors'} />
                            <span className="font-semibold text-sm">{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="lg:col-span-9 bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl min-h-[600px] relative overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2 }}
                            className="space-y-8 relative z-10"
                        >
                            {activeTab === 'basic' && (
                                <div className="space-y-8">
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Product Title" name="product_title" value={formData.product_title} onChange={handleInputChange} placeholder="Product Title" required />
                                        <InputField label="Slug" name="slug" value={formData.slug} onChange={handleInputChange} placeholder="product-slug" />
                                    </div>
                                    <InputField label="Short Description" name="short_description" value={formData.short_description} onChange={handleInputChange} type="textarea" placeholder="Short description..." />
                                    <InputField label="Full Description" name="full_description" value={formData.full_description} onChange={handleInputChange} type="textarea" placeholder="Full description..." />
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Category" name="category_id" value={formData.category_id} onChange={handleInputChange} type="select" options={[{ label: 'Select Category', value: '' }, ...categories]} required />
                                        <InputField label="Product Type" name="product_type" value={formData.product_type} onChange={handleInputChange} type="select" options={productTypes.length > 0 ? productTypes : [{ label: 'Digital', value: 'digital' }]} />
                                        <InputField label="Version" name="version" value={formData.version} onChange={handleInputChange} placeholder="1.0.0" />
                                    </div>
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Status" name="status" value={formData.status} onChange={handleInputChange} type="select" options={[{ label: 'Draft', value: 'draft' }, { label: 'Published', value: 'published' }, { label: 'Archived', value: 'archived' }, { label: 'Private', value: 'private' }]} />
                                        <div className="flex items-center gap-6 flex-1 min-w-[280px] p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                                            <label className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox" name="featured" checked={formData.featured} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-600 text-violet-600 focus:ring-violet-500 bg-slate-700" />
                                                <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">Featured</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox" name="trending" checked={formData.trending} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-600 text-violet-600 focus:ring-violet-500 bg-slate-700" />
                                                <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">Trending</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'pricing' && (
                                <div className="space-y-8">
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Regular Price" name="regular_price" value={formData.regular_price} onChange={handleInputChange} icon={DollarSign} placeholder="99.00" required />
                                        <InputField label="Sale Price" name="sale_price" value={formData.sale_price} onChange={handleInputChange} icon={DollarSign} placeholder="49.00" />
                                        <InputField label="Discount %" name="discount_percentage" value={formData.discount_percentage} onChange={handleInputChange} placeholder="50" />
                                    </div>
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="License Type" name="license_type" value={formData.license_type} onChange={handleInputChange} type="select" options={[{ label: 'Single Use', value: 'single' }, { label: 'Extended', value: 'extended' }, { label: 'Commercial', value: 'commercial' }]} />
                                        <InputField label="Currency" name="currency" value={formData.currency} onChange={handleInputChange} type="select" options={[{ label: 'USD', value: 'USD' }, { label: 'INR', value: 'INR' }]} />
                                        <InputField label="Tax %" name="tax_percentage" value={formData.tax_percentage} onChange={handleInputChange} placeholder="0" />
                                    </div>
                                    <div className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl border border-slate-700">
                                        <input type="checkbox" name="coupon_allowed" checked={formData.coupon_allowed} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-600 text-violet-600 focus:ring-violet-500 bg-slate-700" />
                                        <span className="text-slate-300 font-medium text-sm">Allow System Coupons for this product</span>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'files' && (
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Main Product File</label>
                                        <div className="relative">
                                            <input type="file" onChange={(e) => handleFileChange(e, 'main_file')} className="hidden" id="main-file-upload" />
                                            <label htmlFor="main-file-upload" className="h-40 border-2 border-dashed border-slate-700 rounded-2xl flex flex-col items-center justify-center group hover:border-violet-500/50 transition-all cursor-pointer bg-slate-800/30">
                                                <Download className="text-slate-500 group-hover:text-violet-500 mb-3 transition-all" size={32} />
                                                <p className="text-slate-300 font-medium text-sm">
                                                    {uploadedFiles.main_file ? 'New file selected' : 'Update Main File (Optional)'}
                                                </p>
                                                {uploadedFiles.main_file && <p className="text-violet-400 text-xs mt-1">{uploadedFiles.main_file.name}</p>}
                                            </label>
                                        </div>
                                    </div>
                                    <InputField label="Documentation Link" name="documentation_url" value={formData.documentation_url} onChange={handleInputChange} icon={LinkIcon} placeholder="https://..." />
                                    <InputField label="Demo URL" name="demo_url" value={formData.demo_url} onChange={handleInputChange} icon={Eye} placeholder="https://..." />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InputField label="Download Limit" name="download_limit" value={formData.download_limit} onChange={handleInputChange} placeholder="-1 for infinite" />
                                        <InputField label="Expiry (Days)" name="download_expiry_days" value={formData.download_expiry_days} onChange={handleInputChange} placeholder="-1 for never" />
                                    </div>
                                    <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-800 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Zap className="text-amber-500" size={20} />
                                                <span className="text-white font-bold text-sm">License Key Enforcement</span>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input type="checkbox" name="license_key_required" checked={formData.license_key_required} onChange={handleInputChange} className="sr-only peer" />
                                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                                            </label>
                                        </div>
                                        {formData.license_key_required && (
                                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}>
                                                <InputField label="License Key Prefix" name="license_key_prefix" value={formData.license_key_prefix} onChange={handleInputChange} placeholder="MHTECH-" />
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'seo' && (
                                <div className="space-y-8">
                                    <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
                                        <h3 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                                            <Search size={18} className="text-violet-500" /> SEO Configuration
                                        </h3>
                                        <div className="space-y-6">
                                            <InputField label="Meta Title" name="meta_title" value={formData.meta_title} onChange={handleInputChange} />
                                            <InputField label="Meta Description" name="meta_description" value={formData.meta_description} onChange={handleInputChange} type="textarea" />
                                            <InputField label="Meta Keywords (comma-separated)" name="meta_keywords" value={formData.meta_keywords} onChange={handleInputChange} />
                                            <div className="flex flex-wrap gap-6">
                                                <InputField label="Focus Keyword" name="focus_keyword" value={formData.focus_keyword} onChange={handleInputChange} />
                                                <InputField label="Canonical URL" name="canonical_url" value={formData.canonical_url} onChange={handleInputChange} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="p-6 bg-blue-900/10 rounded-2xl border border-blue-900/20 space-y-4">
                                            <h4 className="text-blue-400 font-bold text-xs uppercase tracking-widest">OG / Facebook Meta</h4>
                                            <InputField label="OG Title" name="og_title" value={formData.og_title} onChange={handleInputChange} />
                                            <InputField label="OG Description" name="og_description" value={formData.og_description} onChange={handleInputChange} type="textarea" />
                                        </div>
                                        <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700 space-y-4">
                                            <h4 className="text-slate-400 font-bold text-xs uppercase tracking-widest">Twitter Card Meta</h4>
                                            <InputField label="Twitter Title" name="twitter_title" value={formData.twitter_title} onChange={handleInputChange} />
                                            <InputField label="Twitter Description" name="twitter_description" value={formData.twitter_description} onChange={handleInputChange} type="textarea" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'media' && (
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Thumbnail</label>
                                        <div className="relative">
                                            <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'thumbnail_image')} className="hidden" id="thumbnail-upload" />
                                            <label htmlFor="thumbnail-upload" className="h-64 border-2 border-dashed border-slate-700 rounded-2xl flex flex-col items-center justify-center group hover:border-violet-500/50 transition-all cursor-pointer bg-slate-800/30 relative overflow-hidden">
                                                {previewUrls.thumbnail_image ? (
                                                    <div className="relative w-full h-full">
                                                        <img src={previewUrls.thumbnail_image} alt="Thumbnail preview" className="w-full h-full object-contain" />
                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <p className="text-white font-medium">Click to change</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Upload size={32} className="text-slate-500" />
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Gallery</label>
                                        <div className="relative">
                                            <input type="file" accept="image/*" multiple onChange={(e) => handleFileChange(e, 'gallery_images')} className="hidden" id="gallery-upload" />
                                            <label htmlFor="gallery-upload" className="h-32 border-2 border-dashed border-slate-700 rounded-2xl flex flex-col items-center justify-center group hover:border-violet-500/50 transition-all cursor-pointer bg-slate-800/30">
                                                <Plus size={28} className="text-slate-500" />
                                                <p className="text-slate-300 font-medium text-sm">Click to Add Gallery Images</p>
                                            </label>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {previewUrls.gallery_images.map((url, index) => (
                                                <div key={index} className="relative aspect-video bg-slate-800 border border-slate-700 rounded-xl overflow-hidden group">
                                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                                    <button type="button" onClick={() => removeGalleryImage(index)} className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 shadow-lg"><X size={14} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <InputField label="Image Alt Text" name="alt_text" value={formData.alt_text} onChange={handleInputChange} placeholder="Descriptive text..." />
                                </div>
                            )}

                            {activeTab === 'features' && (
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Key Features</label>
                                            <button onClick={() => addArrayItem('features')} className="text-violet-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:text-violet-400"><Plus size={14} /> Add Feature</button>
                                        </div>
                                        {formData.features.map((f, i) => (
                                            <div key={i} className="flex gap-2">
                                                <input value={f} onChange={(e) => handleArrayChange(i, e.target.value, 'features')} className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm focus:border-violet-500 outline-none" placeholder="Feature..." />
                                                <button onClick={() => removeArrayItem(i, 'features')} className="p-3 text-slate-500 hover:text-rose-500 transition-colors"><X size={18} /></button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">What is Included</label>
                                            <button onClick={() => addArrayItem('what_is_included')} className="text-violet-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:text-violet-400"><Plus size={14} /> Add Item</button>
                                        </div>
                                        {formData.what_is_included.map((item, i) => (
                                            <div key={i} className="flex gap-2">
                                                <input value={item} onChange={(e) => handleArrayChange(i, e.target.value, 'what_is_included')} className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm focus:border-violet-500 outline-none" placeholder="Included item..." />
                                                <button onClick={() => removeArrayItem(i, 'what_is_included')} className="p-3 text-slate-500 hover:text-rose-500 transition-colors"><X size={18} /></button>
                                            </div>
                                        ))}
                                    </div>
                                    <InputField label="Technical Requirements" name="technical_requirements" value={formData.technical_requirements} onChange={handleInputChange} type="textarea" placeholder="PHP 8.1, Node v18..." />
                                    <InputField label="Compatibility" name="compatibility" value={formData.compatibility} onChange={handleInputChange} placeholder="Chrome, Safari, React..." />
                                </div>
                            )}

                            {activeTab === 'marketing' && (
                                <div className="space-y-8">
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Money Back Policy" name="money_back_guarantee" value={formData.money_back_guarantee} onChange={handleInputChange} placeholder="e.g. 7 Days Guarantee" />
                                        <InputField label="Rating" name="rating" value={formData.rating} onChange={handleInputChange} type="select" options={[{ label: '⭐⭐⭐⭐⭐ 5.0', value: '5' }, { label: '⭐⭐⭐⭐ 4.5', value: '4.5' }, { label: '⭐⭐⭐⭐ 4.0', value: '4' }]} />
                                    </div>
                                    <InputField label="Marketing Badge" name="badge_text" value={formData.badge_text} onChange={handleInputChange} placeholder="e.g. Best Seller / New Release" />
                                    <InputField label="Tags (Comma separated)" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="react, dashboard, tailwind..." />

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">FAQ Items</label>
                                            <button onClick={addFaqItem} className="text-violet-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:text-violet-400"><Plus size={14} /> Add FAQ</button>
                                        </div>
                                        {formData.faq.map((item, i) => (
                                            <div key={i} className="p-4 bg-slate-800 border border-slate-700 rounded-xl space-y-3 relative group">
                                                <button onClick={() => removeFaqItem(i)} className="absolute top-4 right-4 text-slate-500 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"><X size={18} /></button>
                                                <input value={item.q} onChange={(e) => handleFaqChange(i, 'q', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm font-bold" placeholder="Question" />
                                                <textarea value={item.a} onChange={(e) => handleFaqChange(i, 'a', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm h-20 resize-none" placeholder="Answer" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'inventory' && (
                                <div className="space-y-8">
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Stock Status" name="stock_status" value={formData.stock_status} onChange={handleInputChange} type="select" options={[{ label: 'In Stock', value: 'instock' }, { label: 'Out of Stock', value: 'outofstock' }, { label: 'Pre-Order', value: 'preorder' }]} />
                                        <InputField label="Stock Quantity" name="stock_quantity" value={formData.stock_quantity} onChange={handleInputChange} placeholder="999" />
                                    </div>
                                    <div className="flex flex-wrap gap-6">
                                        <InputField label="Visibility" name="visibility" value={formData.visibility} onChange={handleInputChange} type="select" options={[{ label: 'Public', value: 'public' }, { label: 'Private', value: 'private' }, { label: 'Hidden', value: 'hidden' }]} />
                                        <InputField label="Publish Date" name="publish_date" value={formData.publish_date} onChange={handleInputChange} type="datetime-local" />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'advanced' && (
                                <div className="space-y-8">
                                    <div className="p-6 border border-amber-500/20 bg-amber-500/5 rounded-2xl space-y-6">
                                        <h3 className="text-amber-500 font-bold text-sm flex items-center gap-2">
                                            <Zap size={18} /> Enterprise Features
                                        </h3>
                                        <InputField label="Affiliate Commission (%)" name="affiliate_commission" value={formData.affiliate_commission} onChange={handleInputChange} placeholder="15" />
                                        <div className="flex items-center justify-between p-4 bg-slate-800 rounded-xl border border-slate-700">
                                            <span className="text-white font-bold text-sm">Allow API Access</span>
                                            <input type="checkbox" name="api_access" checked={formData.api_access} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-600 text-violet-600 focus:ring-violet-500 bg-slate-700" />
                                        </div>
                                        <div className="flex items-center justify-between p-4 bg-slate-800 rounded-xl border border-slate-700">
                                            <span className="text-white font-bold text-sm">Auto-Update Enabled</span>
                                            <input type="checkbox" name="auto_update_enabled" checked={formData.auto_update_enabled} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-600 text-violet-600 focus:ring-violet-500 bg-slate-700" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default EditProduct;
